import { useCallback, useEffect, useMemo, useRef } from 'react';
import { type FlatListProps } from 'react-native';
import { GoodsCard, List, useScreenSize } from '@banhala/ui-kit';
import { isGoodsCard, type CardEntity } from '../TwoColCardList/types';
import type { GoodsCoreModel, IScreensLogging } from '../../../../api/types';
import { bridge, isNotNil, useNavigator } from '../../../../utils';
import { useAddLikeGoods, type useLikeMap } from '../../../../hooks';
import { AdAction } from '../../../../utils/eventLogger/adLogger/types';
import { logOnGoodsPressItem } from '../ThreeColGoodsList/log/logOnGoodsPressItem';
import { logOnPressLike } from '../ThreeColGoodsList/log/logOnPressLike';
import { useRemoveLikeGoods } from '../../../../queries';
import {
  cardRenderItemGenerator,
  type CardRenderItem,
} from '../TwoColCardList/cardRenderItemGenerator';
import { PrimaryCard } from '../TwoColCardList/PrimaryCard';

const NUM_COLUMNS = 3;
const COLUMN_GAP = 2;

interface ThreeColCardListProps extends ReturnType<typeof useLikeMap> {
  entity: CardEntity[];
}

export const ThreeColCardList = ({
  entity,
  likeMap,
  setLikeMap,
}: ThreeColCardListProps) => {
  const { navigator } = useNavigator();
  const { width: screenWidth } = useScreenSize();

  const itemWidth = useMemo(
    () => (screenWidth - COLUMN_GAP * (NUM_COLUMNS - 1)) / NUM_COLUMNS,
    [screenWidth],
  );

  const goodsEntities = useMemo(() => entity.filter(isGoodsCard), [entity]);

  useEffect(() => {
    setLikeMap(prevState => {
      return {
        ...prevState,
        ...goodsEntities.reduce<Record<string, boolean>>((acc, cur) => {
          acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
          return acc;
        }, {}),
      };
    });
  }, [goodsEntities, setLikeMap]);

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { addLikeGoods } = useAddLikeGoods({
    listParams: goodsEntities[0]?.logging.analytics?.LIST_ID as
      | string
      | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen:
      (goodsEntities[0]?.logging.analytics?.SCREEN as string | undefined) ?? '',
  });

  const showRanking = useMemo(
    () => goodsEntities.some(item => isNotNil(item.render?.data.ranking)),
    [goodsEntities],
  );

  const listData = useMemo(() => cardRenderItemGenerator(entity), [entity]);

  const handlePressItem = useCallback(
    async ({
      logging,
      item,
    }: {
      logging: IScreensLogging;
      item: GoodsCoreModel;
    }) => {
      logOnGoodsPressItem({ item, logging });
      navigator.moveToGoods({
        sno: item.sno,
        firstPageRendering: item.first_page_rendering ?? null,
        loggingAnalytics: logging.analytics,
      });
    },
    [navigator],
  );

  const handlePressLike = useCallback(
    async ({
      logging,
      item,
    }: {
      logging: IScreensLogging;
      item: GoodsCoreModel;
    }) => {
      const currentLike = likeMap[item.sno] ?? false;
      logOnPressLike({ logging, item, currentLike });
      if (currentLike) {
        await mutateRemoveLikeGoods({
          sno: item.sno,
          list_params_referrer: logging.analytics?.LIST_ID as
            | string
            | undefined,
        });
        bridge.likeGoods({
          goods_sno: item.sno,
          is_liked: false,
        });
        setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
        return;
      }
      addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
    },
    [likeMap, mutateRemoveLikeGoods, setLikeMap, addLikeGoods],
  );

  const displayLoggedList = useRef<number[]>([]);

  const onViewableItemsChanged = useCallback<
    NonNullable<FlatListProps<CardRenderItem>['onViewableItemsChanged']>
  >(({ viewableItems }) => {
    viewableItems.forEach(({ index, isViewable, item }) => {
      if (index === null || !isViewable) {
        return;
      }
      if (displayLoggedList.current.includes(index)) {
        return;
      }
      displayLoggedList.current.push(index);

      if (item.type === 'PRIMARY_CARD') {
        bridge.logAnalyticsEvent({
          name: 'DISPLAY_PRIMARY_CARD',
          analytics: item.logging.analytics,
          ably_only_log: item.logging.ably_only_log,
        });
      } else {
        bridge.logAnalyticsEvent({
          name: 'DISPLAY_GOODS',
          analytics: item.logging.analytics,
          ably_only_log: item.logging.ably_only_log,
        });
        if (isNotNil(item.item.ad)) {
          bridge.logAdEvent({
            action: AdAction.DISPLAY,
            ad: item.item.ad,
          });
        }
      }
    });
  }, []);

  const renderItem = ({
    item,
    index,
  }: {
    item: CardRenderItem;
    index: number;
  }) => {
    if (item.type === 'PRIMARY_CARD') {
      return (
        <PrimaryCard
          item={item.item}
          logging={item.logging}
          width={itemWidth}
        />
      );
    }
    return (
      <GoodsCard
        data={item}
        size="medium"
        testID={`goods/${item.sno}?LIST_INDEX=${index}&DELIVERY_TYPE=${item.deliveryType}`}
        defaultWidth={itemWidth}
        ranking={showRanking ? index + 1 : undefined}
        onPress={() => handlePressItem(item)}
        onPressLike={async () => handlePressLike(item)}
        isLike={likeMap[item.sno]}
      />
    );
  };

  return (
    <List<CardRenderItem>
      data={listData}
      numColumns={NUM_COLUMNS}
      columnWrapperStyle={{ gap: COLUMN_GAP }}
      onViewableItemsChanged={onViewableItemsChanged}
      keyExtractor={(item, index) =>
        item.type === 'PRIMARY_CARD'
          ? `primary_${index}`
          : (item.sno ?? index).toString()
      }
      renderItem={renderItem}
    />
  );
};
